import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Package, PackageContext } from "./PackageContext";
import { CheckSelectionStep } from "./components/steps/CheckSelectionStep";
import { PackageDetailsStep } from "./components/steps/PackageDetailsStep";
import { ReviewStep } from "./components/steps/ReviewStep";
import { ConfigurationError } from "../../../components/ConfigurationError";
import { useCanCreatePackages } from "../../../components/permissions/Permissions";
import { AdjudicationStep } from "./components/steps/AdjudicationStep";
import { ATSConfigurationStep } from "./components/steps/ATSConfigurationStep";
import { useFlags } from "launchdarkly-react-client-sdk";
import { shouldShowClientInputConfiguration } from "./package-utils";

export function PackageCreationPage() {
  const { t } = useTranslation();
  const [packageData, setPackage] = useState<Package>({
    name: "",
    description: "",
    checks: [],
    bundles: [],
    tag_names: [],
    rubric_id: null,
    is_ats_package: false,
    is_api_package: false,
    client_input_claims: {},
  });
  const [currentStep, setCurrentStep] = useState(0);

  const { enableClientAdjudication } = useFlags();
  const shouldShowATSConfiguration =
    shouldShowClientInputConfiguration(packageData);

  const steps = [
    <PackageDetailsStep key="PackageDetailsStep" />,
    <CheckSelectionStep key="CheckSelectionStep" />,
    ...(enableClientAdjudication
      ? [<AdjudicationStep key="AdjudicationStep" />]
      : []),
    ...(shouldShowATSConfiguration
      ? [<ATSConfigurationStep key="ATSConfigurationStep" />]
      : []),
    <ReviewStep key="ReviewStep" />,
  ];

  const handleNext = () => {
    setCurrentStep((curr) => curr + 1);
  };

  const handlePrev = () => {
    setCurrentStep((curr) => curr - 1);
  };

  const setClientInputClaims = (claims: Record<string, unknown>) => {
    setPackage((prev) => ({
      ...prev,
      client_input_claims: claims,
    }));
  };

  if (!useCanCreatePackages()) {
    return (
      <ConfigurationError
        message={t(
          "Hmm, it looks like you do not have permission to create packages."
        )}
      />
    );
  }

  return (
    <PackageContext.Provider
      value={{
        packageData,
        setPackage,
        setClientInputClaims,
        goToNextStep: handleNext,
        goToPrevStep: handlePrev,
        currentStep,
      }}
    >
      {steps[currentStep]}
    </PackageContext.Provider>
  );
}
